import { useState, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Sparkle,
  UserCirclePlus,
  TreeStructure,
  CheckCircle,
  MapTrifold,
} from "@phosphor-icons/react";

/**
 * A short, friendly 5-step welcome walkthrough shown once per group to
 * newly-verified members. Persisted by localStorage so it never re-appears
 * for the same user/group pair. Optionally switches tabs as the user
 * progresses so the destination is in view when the modal closes.
 */
const STEPS = [
  {
    key: "welcome",
    icon: Sparkle,
    title: "Welcome — you're in.",
    body: "Let's get you set up in under a minute. We'll show you four quick things, then leave you to it.",
    tab: null,
    cta: "Show me around",
  },
  {
    key: "family-info",
    icon: UserCirclePlus,
    title: "1. Complete your family info",
    body: "In the Members tab, fill in your parents, siblings, spouse and children. We'll auto-match names to anyone already here.",
    tab: "members",
    cta: "Next",
  },
  {
    key: "add-relationships",
    icon: TreeStructure,
    title: "2. Add a relationship",
    body: "Spot a relative? Open Relationships and add a direct claim — parent, sibling, spouse, child, or more.",
    tab: "relationships",
    cta: "Next",
  },
  {
    key: "verify",
    icon: CheckCircle,
    title: "3. Verify your connections",
    body: "Family members confirm each other's claims. Two confirmations — or one from an admin — lock a connection in.",
    tab: "relationships",
    cta: "Next",
  },
  {
    key: "map",
    icon: MapTrifold,
    title: "4. Read the map",
    body: "The map shows verified connections. Gold ring = paternal branch, green = maternal, silver dashed = in memoriam.",
    tab: "map",
    cta: "Got it",
  },
];

export default function OnboardingTour({ groupId, groupName, userId, onTabChange }) {
  const storageKey = `legacylink.tour_v1.${userId}.${groupId}`;
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!userId || !groupId) return;
    try {
      if (!localStorage.getItem(storageKey)) {
        // small delay so the page settles first
        const t = setTimeout(() => setOpen(true), 400);
        return () => clearTimeout(t);
      }
    } catch {
      /* ignore storage errors */
    }
  }, [userId, groupId, storageKey]);

  const finish = () => {
    try {
      localStorage.setItem(storageKey, new Date().toISOString());
    } catch {
      /* ignore */
    }
    setOpen(false);
  };

  const advance = () => {
    const next = step + 1;
    if (next >= STEPS.length) {
      finish();
      return;
    }
    const nextStep = STEPS[next];
    if (nextStep.tab && onTabChange) onTabChange(nextStep.tab);
    setStep(next);
  };

  const current = STEPS[step];
  const Icon = current.icon;

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : finish())}>
      <DialogContent
        className="max-w-md p-0 overflow-hidden bg-ivory border border-sage"
        data-testid="onboarding-tour"
      >
        <div className="px-7 pt-7 pb-3">
          <div className="w-12 h-12 rounded-full bg-gold/15 text-forest flex items-center justify-center mb-4">
            <Icon size={26} weight="duotone" />
          </div>
          <h2
            className="font-display text-2xl text-forest leading-tight mb-2"
            data-testid="tour-title"
          >
            {step === 0 && groupName ? `Welcome to ${groupName}.` : current.title}
          </h2>
          <p className="text-sageGray text-sm leading-relaxed" data-testid="tour-body">
            {current.body}
          </p>
        </div>

        <div className="flex items-center justify-between px-7 py-4 bg-white border-t border-sage">
          <div className="flex items-center gap-1.5" aria-hidden>
            {STEPS.map((s, i) => (
              <span
                key={s.key}
                className={`block h-1.5 rounded-full transition-all ${
                  i === step ? "w-6 bg-forest" : i < step ? "w-1.5 bg-forest/40" : "w-1.5 bg-sage"
                }`}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={finish}
              className="text-xs text-sageGray hover:text-forest underline-offset-2 hover:underline"
              data-testid="tour-skip"
            >
              {step === STEPS.length - 1 ? "Close" : "Skip tour"}
            </button>
            <Button
              size="sm"
              onClick={advance}
              className="bg-forest hover:bg-forestSoft text-ivory rounded-full px-5"
              data-testid="tour-next"
            >
              {current.cta}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
